import { ILog } from "@ihr-radioedit/inferno-core";
import {
  SessionType,
  cookies,
  type Store,
  OneTrustManage,
  CcpaStatus,
  IHRSessionState,
  ihrCookieName,
} from "@inferno/renderer-shared-core";

const log = ILog.logger("onetrust.ts");

declare global {
  interface Window {
    OptanonWrapper: () => void;
    Optanon: {
      ToggleInfoDisplay: () => void;
    };
    OnetrustActiveGroups: string;
    OneTrust: {
      UpdateConsent: (category: string, value: string) => Promise<void>;
      dataSubjectParams: {
        id: string;
        isAnonymous: boolean;
        token: string;
      };
    };
  }
}

export class OneTrustManager extends OneTrustManage {
  protected _library?: Promise<void>;
  private _consentNoticeLibrary?: Promise<void>;
  private _autoBlockLibrary?: Promise<void>;
  private disabled = false;
  private mounted = false;

  constructor(protected store: Store) {
    super();
    store.oneTrust = this;
  }
  static cookieTokenName = "OTTok";
  static cookieIdName = "OTPid";
  static cookieProfileIdName = "pid";
  static cookieUserTypeName = "userType";
  static coastConsentId = "4f76ca8a-4263-4392-8880-c448aa78ab63";
  static localConsentId = "7d044e9d-e966-4b73-b448-a29d06f71027";
  static category = "Category";
  static categoryValue = "C0004";

  private getCookieDomain() {
    const hostname = window.location.hostname;
    if (hostname.endsWith("ihrint.com")) {
      return ".ihrint.com";
    } else if (hostname.endsWith("iheart.com")) {
      return ".iheart.com";
    } else {
      return "";
    }
  }

  private setCookies = (token: string, identifier: string) => {
    const domain = this.getCookieDomain();
    const cookieAttributes = `domain=${domain}; max-age=28800; SameSite=None; Secure; Path=/`;

    cookies().setRaw(OneTrustManager.cookieTokenName, token, cookieAttributes);
    cookies().setRaw(OneTrustManager.cookieIdName, identifier, cookieAttributes);
  };

  private setAttributes = (element: Element, attributes: { [key: string]: string }) => {
    for (const key in attributes) {
      if (attributes.hasOwnProperty(key)) {
        element.setAttribute(key, attributes[key]);
      }
    }
  };

  private getConsentScriptUrl = () => {
    const id =
      this.store.site.config.sections?.partners?.auth_type?.taxo?.name === SessionType.PWS_AUTH
        ? OneTrustManager.coastConsentId
        : OneTrustManager.localConsentId;
    return {
      id,
      url: `${this.store.env.ONETRUST_CONSENT_PATH}/${id}/otSDKStub.js`,
    };
  };

  private makeOptOutUpdateConsent = () => ({
    category: OneTrustManager.category,
    value: `${OneTrustManager.categoryValue}:0`,
  });

  private updateOptOutConsent = () => {
    const { category, value } = this.makeOptOutUpdateConsent();
    window.OneTrust?.UpdateConsent(category, value);
  };

  private async getToken(identifier: string): Promise<string | undefined> {
    const resp = await fetch(this.store.env.ONETRUST_TOKEN_URL + identifier);
    if (resp?.ok) {
      return resp.json();
    }
  }

  private setDataSubjectParams(params: { id: string; isAnonymous: boolean; token: string }) {
    window.OneTrust = {
      ...(window.OneTrust ?? {}),
      dataSubjectParams: params,
    };
  }

  disable = () => {
    log.debug("OneTrust disabled via client/index.tsx");
    this.disabled = true;
    this.store.oneTrustReady = false; // notify react observers to re-render
  };

  setOptOutConsent = (from: "ClientNavigation" | "Load") => {
    // sync if amp shows optout but One trust shows opt in - we need to update OneTrust to opt out
    if (!this.disabled && this.store.isIHRSession === CcpaStatus.OptOut && !this.isOptOut()) {
      log.debug(`Updating OneTrust Consent OptOut On ${from}`);
      this.updateOptOutConsent();
    }
  };

  isOptOut = () => {
    if (this.disabled) {
      log.debug("OneTrust.isOptOut(): OneTrust disabled, defaulting to opted out for privacy compliance");
      return true;
    }

    // OneTrust sets ',,' as a transitional/empty state during initialization
    // Per OneTrust documentation: "If OnetrustActiveGroups contains an empty string with commas,
    // skip it and validate the OptanonConsent cookie as a fallback"
    const activeGroups = window?.OnetrustActiveGroups;

    // Check GPC first - it takes precedence over everything else
    const isGpcEnabled = this.store.checkBrowserGPC();
    if (isGpcEnabled) {
      log.debug("OneTrust.isOptOut(): GPC enabled, user is opted out");
      return true;
    }

    if (!activeGroups || activeGroups === ",," || activeGroups === "") {
      log.debug("OneTrust.isOptOut(): Transitional state, checking OptanonConsent cookie", { activeGroups });

      const cookieResult = OneTrustManager.checkConsentFromCookie(OneTrustManager.categoryValue);
      if (cookieResult !== undefined) {
        log.debug("OneTrust.isOptOut(): Valid data, returning from cookie", { cookieResult });
        return cookieResult;
      }

      log.debug("OneTrust.isOptOut(): No valid data in transitional state, defaulting to opted in (CCPA default)");
      return false;
    }

    const isOptedOut = !activeGroups.includes(OneTrustManager.categoryValue);
    log.debug("OneTrust.isOptOut():", {
      activeGroups,
      isOptedOut,
    });

    return isOptedOut;
  };

  setBootstrap() {
    return new Promise<void>(resolve => {
      window.OptanonWrapper = () => {
        // mounted is needed since OptanonWrapper not only gets called on load but every time you window.OneTrust?.UpdateConsent() :\
        if (!this.mounted) {
          this.setOptOutConsent("Load");
          this.store.oneTrustReady = true; // notify react observers to re-render
          this.mounted = true;
          resolve();
        }
      };
    });
  }

  toggleInfoDisplay = async (e: Event) => {
    e.preventDefault();

    if (!this.disabled && window.Optanon?.ToggleInfoDisplay) {
      window.Optanon.ToggleInfoDisplay();
    } else {
      log.error("Cant toggle, check scripts were loaded correctly");
    }
  };

  async init(store: Store) {
    const oneTrustToken = cookies().get(OneTrustManager.cookieTokenName);
    const oneTrustId = cookies().get(OneTrustManager.cookieIdName);

    // Determine current user state from session
    const profile = (store.session.currentSession?.state as IHRSessionState)?.profile;
    log.debug("🚀 -> profile", profile);
    const userCookie = cookies().getJSON(ihrCookieName);
    log.debug("🚀 -> userCookie", userCookie);

    const profileId = profile?.profileId || userCookie?.user?.profileId;
    const isAnonymous = profile?.isAnonymous ?? userCookie?.user?.isAnonymous ?? true;

    // Case 1: User is authenticated (not anonymous and has profileId)
    if (profileId && !isAnonymous) {
      const identifier = profileId.toString();

      // Check if existing OneTrust cookies match the current user
      if (oneTrustToken && oneTrustId && oneTrustId === identifier) {
        log.debug("OneTrustManager: Valid OneTrust tokens found for current user- setting dataSubjectParams");
        this.setDataSubjectParams({
          id: identifier,
          isAnonymous: false,
          token: oneTrustToken,
        });
      } else {
        // Fetch new tokens (either no tokens or stale tokens from different user)
        if (oneTrustToken || oneTrustId) {
          log.debug("OneTrustManager: Clearing stale OneTrust tokens");
          cookies().delete(OneTrustManager.cookieTokenName);
          cookies().delete(OneTrustManager.cookieIdName);
        }

        log.debug("OneTrustManager: Fetching new OneTrust tokens for authenticated user");
        const tokenResponse = await this.getToken(identifier);
        if (tokenResponse) {
          log.debug("OneTrustManager: New OneTrust tokens retrieved- setting cookies and dataSubjectParams");
          this.setCookies(tokenResponse, identifier);
          this.setDataSubjectParams({
            id: identifier,
            isAnonymous: false,
            token: tokenResponse,
          });
        } else {
          log.debug("OneTrustManager: No OneTrust token returned from server for authenticated user");
        }
      }
    }
    // Case 2: User is anonymous (no profileId or explicitly anonymous)
    else {
      if (oneTrustToken || oneTrustId) {
        log.debug("OneTrustManager: Clearing stale OneTrust tokens for anonymous user");
        cookies().delete(OneTrustManager.cookieTokenName);
        cookies().delete(OneTrustManager.cookieIdName);
      }

      log.debug("OneTrustManager: Setting anonymous user - setting dataSubjectParams");
      this.setDataSubjectParams({
        id: "",
        isAnonymous: true,
        token: "",
      });
    }
  }

  // Ensure library is loaded once and only once
  loadConsentNoticeLibrary = () => {
    if (this._consentNoticeLibrary) {
      return this._consentNoticeLibrary;
    }

    const { env } = this.store;
    this._consentNoticeLibrary = new Promise((resolve, reject) => {
      const body = document.querySelector("body");
      /* make sure this is in sync with the preload link tag set on the server */
      const { url, id } = this.getConsentScriptUrl();

      if (id && url && body) {
        const script = document.createElement("script");
        script.async = true;
        script.type = "text/javascript";
        this.setAttributes(script, {
          "data-domain-script": id,
        });
        script.src = url;
        script.onload = () => {
          log.debug(`OneTrust Consent Script loaded for ${id}`);
          resolve();
        };

        script.onerror = (
          _event: Event | string,
          _source?: string,
          _fileno?: number,
          _columnNumber?: number,
          error?: Error,
        ) => {
          if (error) {
            log.debug(`OneTrust Consent library could not be loaded for ${id}`, error.message);
          }
          reject(new Error(`OneTrust Consent library could not be loaded for ${id}`));
        };

        body.appendChild(script);
      } else {
        log.debug(
          `OneTrust Consent library could not be loaded: body? ${!!body} configured? ${env.ONETRUST_CONSENT_PATH}`,
        );
        reject(new Error("OneTrust Consent library could not be loaded"));
      }
    });

    return this._consentNoticeLibrary;
  };

  // Ensure library is loaded once and only once
  loadLibrary = () => {
    if (this._library) {
      return this._library;
    }

    const { env } = this.store;
    this._library = new Promise((resolve, reject) => {
      const body = document.querySelector("body");
      /* make sure this is in sync with the preload link tag set on the server */
      const oneTrustIabUrl = this.store.env.ONETRUST_IAB_URL;

      if (oneTrustIabUrl && body) {
        const script = document.createElement("script");

        script.type = "text/javascript";
        this.setAttributes(script, {
          "ccpa-opt-out-ids": "C0004",
          "ccpa-opt-out-geo": "all",
          "ccpa-opt-out-lspa": "true",
        });
        script.async = true;
        script.src = oneTrustIabUrl;
        script.onload = () => {
          log.debug("OneTrust IAB Script loaded");
          resolve();
        };

        script.onerror = (
          _event: Event | string,
          _source?: string,
          _fileno?: number,
          _columnNumber?: number,
          error?: Error,
        ) => {
          if (error) {
            log.debug("OneTrust IAB library could not be loaded", error.message);
          }
          reject(new Error("OneTrust IAB library could not be loaded"));
        };

        body.appendChild(script);
      } else {
        log.debug(`OneTrust IAB library could not be loaded: body? ${!!body} configured? ${env.ONETRUST_IAB_URL}`);
        reject(new Error("OneTrust IAB library could not be loaded"));
      }
    });

    return this._library;
  };

  loadAutoBlockLibrary = () => {
    if (this.store.session.currentSession?.name !== "C2CSessionV2") {
      return Promise.resolve();
    }

    if (this._autoBlockLibrary) {
      return this._autoBlockLibrary;
    }

    this._autoBlockLibrary = new Promise((resolve, reject) => {
      const body = document.querySelector("body");
      const oneTrustAutoBlockUrl = this.store.env.ONETRUST_AUTOBLOCK_URL;

      if (oneTrustAutoBlockUrl && body) {
        const script = document.createElement("script");

        script.type = "text/javascript";
        script.async = true;
        script.src = oneTrustAutoBlockUrl;
        script.onload = () => {
          log.debug("OneTrust OtAutoBlock Script loaded");
          resolve();
        };

        script.onerror = (
          _event: Event | string,
          _source?: string,
          _fileno?: number,
          _columnNumber?: number,
          error?: Error,
        ) => {
          if (error) {
            log.debug("OneTrust OtAutoBlock library could not be loaded", error.message);
          }
          reject(new Error("OneTrust OtAutoBlock library could not be loaded"));
        };

        body.appendChild(script);
      } else {
        log.debug(`OneTrust OtAutoBlock library could not be loaded: body? ${!!body}`);
        reject(new Error("OneTrust OtAutoBlock library could not be loaded"));
      }
    });

    return this._autoBlockLibrary;
  };

  trackPageView = () => {
    log.debug("OneTrust trackPageView not implemented.");
  };

  trigger = () => {
    log.debug("OneTrust trigger not implemented.");
  };
}
